import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from './Navbar';
import RestaurantHeader from '../restaurant/RestaurantHeader';
import RestaurantInfo from '../restaurant/RestaurantInfo';
import { usePlaceData } from '../restaurant/UseRestaurantData';
import ReactMarkdown from 'react-markdown';
import Footer from './Footer';
import ReviewsSection from '../restaurant/ReveiwSection';
import ImageGallery from './ImageGallery';
import WriteComment from './WriteComment';
import MapComponent from '../restaurant/MapComponent';
import useUserStore from "../store/userStore";
import { Phone, MapPin } from 'lucide-react';
import useBreadcrumbStore from "./breadcrumbStore";
import { HeartIcon } from '@heroicons/react/24/outline';

export interface ReviewImage {
  image_id: string;
  filename: string;
  file_url: string;
  description?: string | null;
}

export interface Review {
  review_id: string;
  place: string;
  place_name: string;
  user: string;
  user_name: string;
  full_name: string;
  rate: number;
  comment: string;
  date: string;
}

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const PlaceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { restaurant, loading, error } = usePlaceData(id);
  const { user } = useUserStore();
  const userId = user?.userId;

  // وضعیت‌های مربوط به نظرات
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);

  // وضعیت‌های مربوط به صفحه‌بندی
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const writeCommentRef = useRef<HTMLDivElement | null>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const { setBreadcrumbData, clearBreadcrumbData } = useBreadcrumbStore();

  useEffect(() => {
    if (loading) {
      setBreadcrumbData(null, null, true);
    } else if (restaurant) {
      const categoryName = restaurant.categories?.[0]?.name || restaurant.sub_category || null;
      setBreadcrumbData(restaurant.name, categoryName, false);
    }
    
    return () => {
      clearBreadcrumbData();
    };
  }, [loading, restaurant, setBreadcrumbData, clearBreadcrumbData]);

  const scrollToWriteComment = () => {
    writeCommentRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // تابع دریافت نظرات با پشتیبانی از صفحه‌بندی و شافل
  const fetchReviews = async (page: number = 1, append: boolean = false) => {
    if (!id) return;
    
    setIsLoadingReviews(true);
    setReviewError(null);

    try {
      const params = new URLSearchParams({
        place_id: id,
        page: page.toString(),
        page_size: '10',
        shuffle: 'true'
      });

      const res = await fetch(`${BASE_URL}/api/reviews/?${params.toString()}`);
      
      if (!res.ok) throw new Error('Failed to fetch reviews');
      
      const data = await res.json();
      
      setReviews(prev => append ? [...prev, ...data.results] : data.results);
      setTotalPages(Math.ceil(data.count / 10));
      setCurrentPage(page);
      
    } catch (err) {
      console.error(err);
      setReviewError('خطا در دریافت نظرات کاربران.');
    } finally {
      setIsLoadingReviews(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchReviews(1, false);
    }
  }, [id]);

  const handlePageChange = (page: number) => {
    if (page <= totalPages && !isLoadingReviews) {
      fetchReviews(page, true);
    }
  };

  const handleReviewSubmitted = useCallback((newReview: Review) => {
    
    setReviews(prevReviews => {
      // جلوگیری از تکرار کامنت
      const exists = prevReviews.some(r => r.review_id === newReview.review_id);
      if (exists) {
        console.log("⚠️ کامنت تکراری است");
        return prevReviews;
      }
      
      // اضافه کردن کامنت جدید به ابتدای لیست
      const updatedReviews = [newReview, ...prevReviews];
      return updatedReviews;
    });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-8 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-lootka-green"></div>
      </div>
    );
  }

  if (error || !restaurant) {
    return (
      <div className="min-h-screen bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-myYekanDemibold text-gray-900">مکان مورد نظر پیدا نشد.</h1>
          <p className="text-gray-600 mt-2">{error}</p>
          <Link
            to="/restaurant"
            className="font-myYekanRegular hover:underline mt-4 inline-block"
          >
            بازگشت
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white w-full px-[16px] tablet:px-20 desktop:px-36 py-1 pb-24">
      <Navbar />
      
      <div className="pt-0 md:mt-2 mt-8 w-full flex flex-col gap-6">
        {/* Header and Image Section */}
        <div className="w-full">
          <div className="relative">
            <RestaurantHeader restaurant={restaurant} onWriteCommentClick={scrollToWriteComment} />
            <button className="md:hidden absolute top-16 left-4 z-10">
              <HeartIcon className=" w-[24px] h-[24px] sm:w-[32px] s:h-[32px] ml-1 text-white" />
            </button>
          </div>
          <ImageGallery
            images={restaurant.images || []}
            restaurantName={restaurant.name}
            baseUrl={BASE_URL}
          />
        </div>

        {/* Main Content Layout */}
        <div className='flex flex-col lg:flex-row gap-6'>
          {/* Left Column */}
          <div className="w-full lg:w-2/3 flex flex-col">
            {/* Description Section */}
            <div className="w-full">
              <div className="text-gray-700 desktop:text-[16px] text-sm text-justify leading-relaxed font-myYekanFaNumRegular bg-[#F6FDFE] p-[16px] rounded-[12px]">
                <div className="flex flex-wrap">
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isDescriptionExpanded ? '' : 'line-clamp-4 lg:line-clamp-none'
                    }`}
                  >
                    <ReactMarkdown>{restaurant.description}</ReactMarkdown>
                  </div>
                  {!isDescriptionExpanded && (
                    <button
                      onClick={() => setIsDescriptionExpanded(true)}
                      className="lg:hidden font-myYekanFaNumDemiBold text-sm focus:outline-none py-1 text-blue-600 self-end"
                    >
                      مشاهده بیشتر...
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Info Section (Mobile/Tablet) */}
            <div className="w-full mt-6 lg:hidden">
              <RestaurantInfo restaurant={restaurant} />
              <div className='my-2'>
                {restaurant.latitude && restaurant.longitude && (
                  <MapComponent
                    latitude={restaurant.latitude}
                    longitude={restaurant.longitude}
                    name={restaurant.name}
                  />
                )}
              </div>
            </div>

            {/* Reviews Section */}
            <div className="w-full mt-4">
              <div ref={writeCommentRef}>
                <span className="font-myYekanDemibold text-[16px] md:text-[20px]">
                  به {restaurant.name} امتیاز دهید:
                </span>
                {id && (
                  <WriteComment
                    placeId={id}
                    userId={userId}
                    onReviewSubmitted={handleReviewSubmitted}
                  />
                )}
              </div>
              
              <ReviewsSection
                reviewSummary={restaurant.review_summary}
                reviews={reviews}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isLoading={isLoadingReviews}
              />
            </div>
          </div>

          {/* Right Column: Info (Desktop Only) */}
          <div className="hidden lg:block w-1/3 mt-2">
            <RestaurantInfo restaurant={restaurant} />
            <div className='mt-4'>
              {restaurant.latitude && restaurant.longitude && (
                <MapComponent
                  latitude={restaurant.latitude}
                  longitude={restaurant.longitude}
                  name={restaurant.name}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Action Bar */}
      <div className="font-myYekanDemibold md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-2 z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
        <div className="flex gap-2">
          <a
            href={`geo:${restaurant.latitude},${restaurant.longitude}?q=${restaurant.latitude},${restaurant.longitude}(${encodeURIComponent(restaurant.name)})`}
            className="flex-1 bg-white text-[#4793A8] border border-[#4793A8] rounded-xl py-3 flex items-center justify-center gap-2 font-myYekanDemiBold hover:bg-[#f0f9fa] transition-colors"
            style={{ color: '#4793A8' }}
          >
            <MapPin size={20} />
            <span>مسیریابی</span>
          </a>
          {restaurant.phone && (
            <a
              href={`tel:${restaurant.phone}`}
              className={`flex items-center justify-center gap-2 font-myYekanDemiBold transition-colors rounded-xl py-3 ${
                restaurant.phone
                  ? 'flex-1 bg-[#4793A8] border border-[#4793A8]'
                  : 'w-full bg-[#4793A8]'
                } text-white hover:bg-[#3b7a8c]`}
              style={{ color: 'white' }}
            >
              <Phone size={20} />
              <span>تماس</span>
            </a>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PlaceDetail;